import ApiService from './ApiService'
import OpsApiError from './OpsApiError'
import { DirectUpload } from 'activestorage'
import {
  classifyFetchFailure,
  classifyHttpFailure,
  isConnectivityError,
} from './connectivityErrors'

class MerchantOpsApi extends ApiService {
  constructor() {
    super()
    const baseApiUrl = this.api_base_url || '/api/v1'
    this.merchant_api_base_url = baseApiUrl.replace(/\/v1\/?$/, '/merchant/v1')
  }

  merchantPath = (resource) => `${this.merchant_api_base_url}/${resource}`

  normalizeError = (error) => {
    if (isConnectivityError(error)) return error
    if (error instanceof OpsApiError) return error
    return new OpsApiError(error)
  }

  safeRequest = async (requestFn) => {
    try {
      return await requestFn()
    } catch (error) {
      throw this.normalizeError(error)
    }
  }

  showMerchant = async () => {
    const request = this.prepareGetRequestWithBase('merchant', {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  listCategories = async () => {
    const request = this.prepareGetRequestWithBase('categories', {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  listTimeZones = async () => {
    const request = this.prepareGetRequestWithBase('time_zones', {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  discoverApps = async ({ query = '', categoryIds = [] } = {}) => {
    const params = {}
    if (query) params.query = query
    if (Array.isArray(categoryIds) && categoryIds.length > 0) {
      params.category_ids = categoryIds.join(',')
    }
    const request = this.prepareGetRequestWithBase('discover_apps', params, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  showDiscoverApp = async (appIdOrSlug) => {
    const request = this.prepareGetRequestWithBase('discover_apps', { id: appIdOrSlug }, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  updateMerchant = async (attributes) => {
    return this.safeRequest(() => this.putWithBaseNoId('merchant', attributes, this.merchant_api_base_url))
  }

  listMerchantApps = async () => {
    const request = this.prepareGetRequestWithBase('merchant_apps', {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  listStoreApps = async ({ storeType = 'venue_wedding' } = {}) => {
    const request = this.prepareGetRequestWithBase('store_apps', { store_type: storeType }, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  assignMerchantApp = async (appId) => {
    return this.safeRequest(() =>
      this.postWithBase('merchant_apps', { app_id: appId }, this.merchant_api_base_url)
    )
  }

  removeMerchantApp = async (merchantAppIdOrAppId) => {
    return this.safeRequest(() =>
      this.httpDeleteWithBase(`merchant_apps/${merchantAppIdOrAppId}`, this.merchant_api_base_url)
    )
  }

  listVenues = async () => {
    const request = this.prepareGetRequestWithBase('venues', {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  createVenue = async (attributes) => {
    return this.safeRequest(() => this.postWithBase('venues', attributes, this.merchant_api_base_url))
  }

  showVenue = async (venueId) => {
    const request = this.prepareGetRequestWithBase('venues', { id: venueId }, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  updateVenue = async (venueId, attributes) => {
    return this.safeRequest(() => this.putWithBase('venues', venueId, attributes, this.merchant_api_base_url))
  }

  deleteVenue = async (venueId) => {
    return this.safeRequest(() => this.httpDeleteWithBase(`venues/${venueId}`, this.merchant_api_base_url))
  }

  listVenueZones = async (venueId) => {
    const request = this.prepareGetRequestWithBase(`venues/${venueId}/venue_zones`, {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  listVenueZoneEventHostInvitations = async (venueId, venueZoneId) => {
    const request = this.prepareGetRequestWithBase(
      `venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations`,
      {},
      this.merchant_api_base_url
    )
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  showVenueZoneEventHostInvitation = async (venueId, venueZoneId, invitationId) => {
    const request = this.prepareGetRequestWithBase(
      `venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations`,
      { id: invitationId },
      this.merchant_api_base_url
    )
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  createVenueZoneEventHostInvitation = async (
    venueId,
    venueZoneId,
    { email, invitedUserId = null, eventRoleIds = [] } = {}
  ) => {
    const payload = { email }
    if (invitedUserId) payload.invited_user_id = invitedUserId
    if (Array.isArray(eventRoleIds) && eventRoleIds.length > 0) payload.event_role_ids = eventRoleIds
    return this.safeRequest(() =>
      this.postWithBase(
        `venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations`,
        payload,
        this.merchant_api_base_url
      )
    )
  }

  updateVenueZoneEventHostInvitation = async (
    venueId,
    venueZoneId,
    invitationId,
    { eventRoleIds = [] } = {}
  ) => {
    const payload = {}
    if (Array.isArray(eventRoleIds)) payload.event_role_ids = eventRoleIds

    return this.safeRequest(() =>
      this.putWithBase(
        `venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations`,
        invitationId,
        payload,
        this.merchant_api_base_url
      )
    )
  }

  cancelVenueZoneEventHostInvitation = async (venueId, venueZoneId, invitationId) => {
    return this.safeRequest(() =>
      this.httpDeleteWithBase(
        `venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations/${invitationId}`,
        this.merchant_api_base_url
      )
    )
  }

  resendVenueZoneEventHostInvitation = async (venueId, venueZoneId, invitationId) => {
    const request = new Request(
      this.merchantPath(`venues/${venueId}/venue_zones/${venueZoneId}/event_host_invitations/${invitationId}/resend`),
      {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(this.formatPayload('event_host_invitations', {}, {}))
      }
    )
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  assignVenueZone = async (venueId, { appId, eventHostId = null, zoneName = '', zoneDescription = '', maxParticipationRadius = null }) => {
    const payload = { app_id: appId }
    if (eventHostId) payload.event_host_id = eventHostId
    if (zoneName) payload.zone_name = zoneName
    if (zoneDescription) payload.zone_description = zoneDescription
    if (maxParticipationRadius !== null && maxParticipationRadius !== undefined && maxParticipationRadius !== '') {
      payload.max_participation_radius = maxParticipationRadius
    }
    return this.safeRequest(() => this.postWithBase(`venues/${venueId}/venue_zones`, payload, this.merchant_api_base_url))
  }

  removeVenueZone = async (venueId, venueZoneIdOrZoneId) => {
    return this.safeRequest(() =>
      this.httpDeleteWithBase(`venues/${venueId}/venue_zones/${venueZoneIdOrZoneId}`, this.merchant_api_base_url)
    )
  }

  requestVenueMediaUpload = async (venueId, { filename, contentType, byteSize, purpose = 'cover_photo' }) => {
    const json = await this.safeRequest(() =>
      this.postWithBase(
        'venue_media_uploads',
        {
          venue_id: venueId,
          filename,
          content_type: contentType,
          byte_size: byteSize,
          purpose
        },
        this.merchant_api_base_url
      )
    )

    const attrs = json?.data?.attributes || {}
    return {
      uploadUrl: attrs['upload-url'],
      uploadFields: attrs['upload-fields'] || {},
      finalUrl: attrs['final-url'],
      key: attrs['key'],
      expiresAt: attrs['expires-at'],
      maxBytes: attrs['max-bytes']
    }
  }

  directVenueUpload = async (file, { purpose = 'cover_photo' } = {}) => {
    const upload = new DirectUpload(
      file,
      this.merchantPath('direct_uploads'),
      {
        directUploadWillCreateBlobWithXHR: (xhr) => {
          xhr.setRequestHeader('Authorization', this.getAuthorizationBearer())
          xhr.setRequestHeader('X-Api-Key', process.env.REACT_APP_ANEARAPP_API_KEY)
          xhr.setRequestHeader('Accept', 'application/json')
        },
        blobRecord: {
          metadata: { purpose }
        }
      }
    )

    return this.createUpload(upload)
  }

  getVenueQrPlacard = async (venueId) => {
    const request = this.prepareGetRequestWithBase(`venues/${venueId}/qr_placard`, {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  listVenuePlacards = async (venueId) => {
    const request = this.prepareGetRequestWithBase(`venues/${venueId}/placards`, {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  showVenuePlacard = async (venueId, formatKey) => {
    const request = this.prepareGetRequestWithBase(`venues/${venueId}/placards/${encodeURIComponent(formatKey)}`, {}, this.merchant_api_base_url)
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  updateVenuePlacard = async (venueId, formatKey, attributes = {}) => {
    const payload = this.formatPayload('placards', attributes, {})
    const request = new Request(`${this.merchant_api_base_url}/venues/${venueId}/placards/${encodeURIComponent(formatKey)}`, {
      method: 'PUT',
      headers: this.default_headers,
      body: JSON.stringify(payload)
    })
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  generateVenuePlacard = async (venueId, formatKey, attributes = {}) => {
    const payload = this.formatPayload('placards', attributes, {})
    const request = new Request(`${this.merchant_api_base_url}/venues/${venueId}/placards/${encodeURIComponent(formatKey)}/generate`, {
      method: 'POST',
      headers: this.default_headers,
      body: JSON.stringify(payload)
    })
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  generateVenueQrPlacard = async (venueId, { borderStyle = 'floral_gold', printPhotoUrl = null, testMode = false } = {}) => {
    const attrs = { border_style: borderStyle }
    if (printPhotoUrl) attrs.print_photo_url = printPhotoUrl
    if (testMode) attrs.test_mode = true
    const payload = this.formatPayload('qr_placards', attrs, {})
    const request = new Request(`${this.merchant_api_base_url}/venues/${venueId}/qr_placard`, {
      method: 'POST',
      headers: this.default_headers,
      body: JSON.stringify(payload)
    })
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  downloadVenueQrPdf = async (venueId, fallbackFilename = '') => {
    const request = new Request(`${this.merchant_api_base_url}/venues/${venueId}/qr_pdf`, {
      method: 'GET',
      headers: new Headers({
        ...this.defaultHeaderObject,
        Accept: 'application/pdf'
      })
    })

    try {
      this.setAuthorizationHeader(request)
      let response
      try {
        response = await fetch(request)
      } catch (fetchErr) {
        const classified = classifyFetchFailure(fetchErr)
        throw classified || fetchErr
      }

      const gatewayError = classifyHttpFailure(response.status)
      if (gatewayError) {
        throw gatewayError
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const json = await response.json()
          throw new OpsApiError(json)
        }
        const text = await response.text()
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
      }

      const blob = await response.blob()
      const disposition = response.headers.get('content-disposition') || ''
      const utf8FilenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i)
      const decodedFilename = utf8FilenameMatch?.[1] ? decodeURIComponent(utf8FilenameMatch[1]) : null

      return {
        blob,
        filename: decodedFilename || filenameMatch?.[1] || fallbackFilename || `venue-${venueId}-venue-qr-code-12-12.pdf`
      }
    } catch (error) {
      throw this.normalizeError(error)
    }
  }

  transitionVenueEvent = async (eventId, state) => {
    const payload = this.formatPayload('transitions', { state }, {})
    const request = new Request(
      this.merchantPath(`events/${eventId}/transition`),
      {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.safeRequest(() => this.issueRequest(request, false))
  }

  prepareGetRequestWithBase = (resource, params = {}, baseUrl) => {
    let idParam = ''
    if (params.id) {
      idParam = `/${params.id}`
      delete params.id
    }
    const query = new URLSearchParams(params).toString()
    const querySuffix = query ? `?${query}` : ''
    return new Request(
      `${baseUrl}/${resource}${idParam}${querySuffix}`,
      { method: 'GET', headers: this.default_headers }
    )
  }

  postWithBase = async (resource, attributes, baseUrl, relationships = {}) => {
    const payload = this.formatPayload(resource, attributes, relationships)
    const request = new Request(
      `${baseUrl}/${resource}`,
      {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, false)
  }

  putWithBase = async (resource, id, attributes, baseUrl, relationships = {}) => {
    const payload = this.formatPayload(resource, attributes, relationships)
    const request = new Request(
      `${baseUrl}/${resource}/${id}`,
      {
        method: 'PUT',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, false)
  }

  putWithBaseNoId = async (resource, attributes, baseUrl, relationships = {}) => {
    const payload = this.formatPayload(resource, attributes, relationships)
    const request = new Request(
      `${baseUrl}/${resource}`,
      {
        method: 'PUT',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, false)
  }

  httpDeleteWithBase = async (resource, baseUrl, id = null) => {
    const idStr = id ? `/${id}` : ''
    const request = new Request(
      `${baseUrl}/${resource}${idStr}`,
      { method: 'DELETE', headers: this.default_headers }
    )
    return this.issueRequest(request, false)
  }
}

const merchantOpsApi = new MerchantOpsApi()
export default merchantOpsApi
